import { formatMinor } from './money';
import { fill, message, type Language } from '../i18n/messages';

/** Over or short as a till says it: "Over 40.00", "Short 10.00" or "Even". */
export function overShort(language: Language, minor: number, minorDigits: number): string {
	return signedFigure(language, minor, minorDigits, 'shiftOver');
}

/** Even at 0, else the positive key's or Short's words with the amount. */
function signedFigure(
	language: Language,
	minor: number,
	minorDigits: number,
	positive: 'shiftOver' | 'shiftsDriverExtra',
): string {
	if (minor === 0) return message(language, 'shiftEven');
	return fill(
		message(language, minor > 0 ? positive : 'shiftShort'),
		formatMinor(Math.abs(minor), minorDigits),
	);
}

/** One row of a close's split of Due at close (UC-63.R7): the driver's name, or "No driver", or "Not a delivery". */
export function dueRowLabel(
	language: Language,
	row: { kind: 'driver' | 'none' | 'other'; name?: string },
): string {
	if (row.kind === 'none') return message(language, 'shiftsDueNoDriver');
	if (row.kind === 'other') return message(language, 'shiftsDueNotDelivery');
	return row.name ?? '';
}

/**
 * A driver's short or extra on D27 (UC-63.R8): "Extra 40.00", "Short 10.00" or "Even".
 * An extra is unexplained cash, never revenue or a tip, so it never reads "Over".
 */
export function driverShortExtra(language: Language, minor: number, minorDigits: number): string {
	return signedFigure(language, minor, minorDigits, 'shiftsDriverExtra');
}

/** The part of a closed shift's report D27's Drivers section reads. */
interface DriverRows {
	closedAt?: string;
	drivers?: { shortExtraMinor: number }[] | null;
}

/** Whether D27 shows its Drivers section: a closed shift whose close kept driver rows. */
export function showsDrivers(report: DriverRows): boolean {
	return !!report.closedAt && (report.drivers?.length ?? 0) > 0;
}

/** Whether a row reads extra, which D27 marks and follows with its line (reports.md). */
export function readsExtra(row: { shortExtraMinor: number }): boolean {
	return row.shortExtraMinor > 0;
}

/** Whether any row of a shown Drivers section reads extra. */
export function anyExtra(report: DriverRows): boolean {
	return showsDrivers(report) && (report.drivers ?? []).some(readsExtra);
}
